import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { getShiftReview } from 'features/operations/opsAnalyticsSlice'
import { AppDispatch, RootState } from 'store'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { IOpsShiftScorecardRow, OpsLossBucket } from 'types/opsAnalytics.interface'
import { LOSS_BUCKET_META } from '../Shared/lossClassifier'
import { LOSS_BUCKET_TO_UI } from '../Shared/opsEnumMaps'
import { LossWaterfall } from './LossWaterfall'

const scorecardColumns: IHonestTableColumn<IOpsShiftScorecardRow & { id: number }>[] = [
  {
    id: 'metric',
    header: 'Metric',
    cell: (item) => <BodyText weight='600'>{item.label}</BodyText>,
  },
  {
    id: 'result',
    header: 'Result',
    cell: (item) => (
      <BodyText weight='700' color={tokens.status[item.tone].fg}>
        {item.value}
      </BodyText>
    ),
  },
  {
    id: 'target',
    header: 'Target',
    cell: (item) => <OtherText grey>{item.target ?? '—'}</OtherText>,
  },
  {
    id: 'avg5',
    header: '5-shift avg',
    cell: (item) => <OtherText grey>{item.avg5 ?? '—'}</OtherText>,
  },
]

export const ShiftReview = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { shiftReview, loaded, isLoading } = useSelector((s: RootState) => s.opsAnalytics)

  useEffect(() => {
    const shiftDate = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
    dispatch(getShiftReview({ shiftDate }))
  }, [dispatch])

  const waterfallBars = useMemo(() => {
    if (!shiftReview) return []
    const bars = [
      { label: 'Target', tonnes: shiftReview.waterfall.target, bucket: 'TARGET' as const, drillPath: null },
      ...shiftReview.waterfall.buckets.map((b) => ({
        label: LOSS_BUCKET_META[LOSS_BUCKET_TO_UI[b.bucket] as OpsLossBucket]?.label ?? String(b.bucket),
        tonnes: -b.costValue,
        bucket: (LOSS_BUCKET_TO_UI[b.bucket] ?? 'NO_TASK') as OpsLossBucket,
        drillPath: null,
      })),
      { label: 'Actual', tonnes: shiftReview.waterfall.actual, bucket: 'ACTUAL' as const, drillPath: null },
    ]
    if (shiftReview.waterfall.unattributed) {
      bars.splice(bars.length - 1, 0, {
        label: 'Unattributed',
        tonnes: -shiftReview.waterfall.unattributed,
        bucket: 'UNATTRIBUTED' as OpsLossBucket,
        drillPath: null,
      })
    }
    return bars
  }, [shiftReview])

  const scorecardRows = useMemo(() => {
    if (!shiftReview) return []
    return [
      {
        id: 1,
        label: 'Tonnes',
        value: String(shiftReview.actualTonnes),
        target: String(shiftReview.targetTonnes),
        avg5: String(shiftReview.fiveShiftAvgTonnes),
        unit: 't',
        tone: (shiftReview.actualTonnes >= shiftReview.targetTonnes ? 'success' : 'warning') as IOpsShiftScorecardRow['tone'],
      },
    ]
  }, [shiftReview])

  if (loaded && !isLoading && !shiftReview) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Shift review
          </Head1>
          <NoData text='The shift review generates automatically at shift end.' />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
          Shift review
        </Head1>
        <OtherText grey>
          {shiftReview?.shiftDate ?? 'Latest shift'} · {shiftReview?.provisional ? 'provisional' : 'finalized'}
        </OtherText>
        <Flex column gap='1rem' mt='16' width='100%'>
          <Container>
            <ContainerHeader>
              <TitleText>Scorecard</TitleText>
            </ContainerHeader>
            <ContainerContent>
              {scorecardRows.length ? (
                <HonestTable columns={scorecardColumns} data={scorecardRows} keyField='id' />
              ) : (
                <NoData text='Scorecard populates after shift close.' />
              )}
            </ContainerContent>
          </Container>
          <Container>
            <ContainerHeader>
              <TitleText>Where the tonnes went</TitleText>
            </ContainerHeader>
            <ContainerContent>
              {waterfallBars.length ? (
                <LossWaterfall bars={waterfallBars} />
              ) : (
                <NoData text='Waterfall appears once loss buckets are materialized.' />
              )}
            </ContainerContent>
          </Container>
          {shiftReview?.exceptions?.length ? (
            <Container>
              <ContainerHeader>
                <TitleText>Exceptions</TitleText>
              </ContainerHeader>
              <ContainerContent>
                {shiftReview.exceptions.map((text, index) => (
                  <BodyText key={index}>{text}</BodyText>
                ))}
              </ContainerContent>
            </Container>
          ) : null}
        </Flex>
      </PageWrapper>
    </>
  )
}
